import { useState, useEffect } from 'react';
import { purchaseOrdersAPI, settingsAPI } from '../api/client';
import { useModules } from '../modules/useModules';

export default function CompanySettings() {
  const { isEnabled } = useModules();

  // SSO / Azure AD
  const [sso, setSso] = useState({ azureAdTenantId: '', azureAdClientId: '', ssoEnabled: false, allowLocalLogin: true });
  const [ssoLoading, setSsoLoading] = useState(true);
  const [ssoSaving, setSsoSaving] = useState(false);
  const [ssoMsg, setSsoMsg] = useState({ type: '', text: '' });

  // PO settings
  const [po, setPo] = useState({ mode: null, enforcement: 'warn', tolerancePct: 0 });
  const [poSaving, setPoSaving] = useState(false);
  const [poSyncing, setPoSyncing] = useState(false);
  const [poMsg, setPoMsg] = useState({ type: '', text: '' });

  useEffect(() => {
    // Load SSO (company-level)
    settingsAPI.getCompanyAuth()
      .then(r => {
        const d = r.data?.authSettings || r.data || {};
        setSso({
          azureAdTenantId: d.AzureAdTenantId || '',
          azureAdClientId: d.AzureAdClientId || '',
          ssoEnabled: !!d.IsActive,
          allowLocalLogin: true
        });
      })
      .catch(() => {})
      .finally(() => setSsoLoading(false));

    // Load PO settings
    if (isEnabled('purchase_orders')) {
      purchaseOrdersAPI.getSettings()
        .then(r => setPo(r.data))
        .catch(() => {});
    }
  }, []);

  const saveSso = async () => {
    setSsoSaving(true);
    setSsoMsg({ type: '', text: '' });
    try {
      await settingsAPI.updateCompanyAuth({
        azureAdTenantId: sso.azureAdTenantId,
        azureAdClientId: sso.azureAdClientId,
        ssoEnabled: sso.ssoEnabled,
        autoProvisionEnabled: true,
        requireAdminApproval: false,
        enforceMFA: false,
        sessionTimeoutMinutes: 480
      });
      setSsoMsg({ type: 'success', text: 'SSO settings saved.' });
    } catch (err) {
      setSsoMsg({ type: 'error', text: err.response?.data?.message || 'Failed to save SSO settings' });
    } finally {
      setSsoSaving(false);
    }
  };

  const savePO = async () => {
    setPoSaving(true);
    setPoMsg({ type: '', text: '' });
    try {
      await purchaseOrdersAPI.updateSettings(po);
      setPoMsg({ type: 'success', text: 'Purchase Order settings saved.' });
    } catch (err) {
      setPoMsg({ type: 'error', text: err.response?.data?.message || 'Failed to save PO settings' });
    } finally {
      setPoSaving(false);
    }
  };

  const syncERP = async () => {
    setPoSyncing(true);
    setPoMsg({ type: '', text: '' });
    try {
      const r = await purchaseOrdersAPI.erpSync();
      setPoMsg({ type: 'success', text: `ERP sync complete: ${r.data.created} created, ${r.data.updated} updated${r.data.errors?.length ? `, ${r.data.errors.length} error(s)` : ''}.` });
    } catch (err) {
      setPoMsg({ type: 'error', text: err.response?.data?.message || 'ERP sync failed' });
    } finally {
      setPoSyncing(false);
    }
  };

  const msgCls = (type) =>
    `mb-4 px-4 py-3 rounded-lg text-sm ${type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`;

  return (
    <div className="p-6 max-w-3xl space-y-6">
      <div className="mb-2">
        <h1 className="text-2xl font-bold text-gray-900">Company Settings</h1>
        <p className="text-gray-500 text-sm mt-1">Configure SSO, Purchase Orders, and other company-level settings.</p>
      </div>

      {/* ── Azure AD / SSO ─────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-1">Azure AD / Single Sign-On</h2>
        <p className="text-sm text-gray-500 mb-4">Allow company users to log in with their Microsoft accounts.</p>

        {ssoMsg.text && <div className={msgCls(ssoMsg.type)}>{ssoMsg.text}</div>}

        {ssoLoading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <input
                id="ssoEnabled"
                type="checkbox"
                checked={!!sso.ssoEnabled}
                onChange={e => setSso(s => ({ ...s, ssoEnabled: e.target.checked }))}
                className="w-4 h-4 rounded border-gray-300 text-indigo-600"
              />
              <label htmlFor="ssoEnabled" className="text-sm font-medium text-gray-700">Enable Azure AD SSO</label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tenant ID</label>
                <input
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  value={sso.azureAdTenantId || ''}
                  onChange={e => setSso(s => ({ ...s, azureAdTenantId: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
                <input
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full"
                  placeholder="App registration client ID"
                  value={sso.azureAdClientId || ''}
                  onChange={e => setSso(s => ({ ...s, azureAdClientId: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={saveSso}
                disabled={ssoSaving}
                className="px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50 font-medium"
              >
                {ssoSaving ? 'Saving…' : 'Save SSO Settings'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Purchase Orders ────────────────────────────────────────── */}
      {isEnabled('purchase_orders') && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Purchase Orders</h2>
          <p className="text-sm text-gray-500 mb-4">Configure how purchase orders are managed in your company.</p>

          {poMsg.text && <div className={msgCls(poMsg.type)}>{poMsg.text}</div>}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PO Mode</label>
              <select
                value={po.mode || ''}
                onChange={e => setPo(s => ({ ...s, mode: e.target.value || null }))}
                className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">Disabled (plain text reference only)</option>
                <option value="internal">Internal — SpocoDoc manages POs</option>
                <option value="erp_sync">ERP Sync — read-only from Dynamics BC / Xero</option>
                <option value="both">Both — internal + ERP sync</option>
              </select>
            </div>

            {po.mode && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Enforcement</label>
                  <select
                    value={po.enforcement || 'warn'}
                    onChange={e => setPo(s => ({ ...s, enforcement: e.target.value }))}
                    className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="warn">Warn — show warning but allow submission</option>
                    <option value="block">Block — prevent invoice submission without a PO</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice tolerance (%)</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min="0" max="100" step="0.5"
                      value={po.tolerancePct || 0}
                      onChange={e => setPo(s => ({ ...s, tolerancePct: parseFloat(e.target.value) || 0 }))}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <span className="text-sm text-gray-500">% above PO total allowed when matching invoices</span>
                  </div>
                </div>

                {(po.mode === 'erp_sync' || po.mode === 'both') && (
                  <div>
                    <button
                      type="button"
                      onClick={syncERP}
                      disabled={poSyncing}
                      className="px-4 py-2 border border-purple-300 text-purple-700 rounded-lg text-sm hover:bg-purple-50 disabled:opacity-50"
                    >
                      {poSyncing ? 'Syncing…' : 'Sync POs from ERP now'}
                    </button>
                    <p className="text-xs text-gray-400 mt-1">Reads POs from Dynamics BC / Xero using your Finance Integration credentials</p>
                  </div>
                )}
              </>
            )}

            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={savePO}
                disabled={poSaving}
                className="px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50 font-medium"
              >
                {poSaving ? 'Saving…' : 'Save PO Settings'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
